import { observable } from '@legendapp/state';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { updateSuperProperties, updateUserProperties } from '../lib/analytics';

const CACHE_KEY = 'mellow-languages-cache';

// Read cached languages to prevent onboarding flash on slow networks
function loadCachedLanguages() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors, fall through to default
  }
  return [];
}

function saveCachedLanguages(items) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(items));
  } catch {
    // localStorage full or unavailable, non-critical
  }
}

function clearCachedLanguages() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // non-critical
  }
}

const cachedItems = loadCachedLanguages();

// Languages state
// If we have cached data, start with isLoading: false to avoid onboarding flash
export const languages$ = observable({
  items: cachedItems,
  isLoading: cachedItems.length === 0,
  selectedCode: cachedItems.find(l => l.is_selected)?.language_code ?? cachedItems[0]?.language_code ?? null,
});

// Fetch user languages
export const fetchLanguages = async () => {
  const user = auth$.user.get();
  if (!user) {
    languages$.items.set([]);
    languages$.isLoading.set(false);
    clearCachedLanguages();
    return;
  }

  // Only show loading spinner if we have no cached data
  if (languages$.items.get().length === 0) {
    languages$.isLoading.set(true);
  }

  const { data, error } = await supabase
    .from('mellow_user_languages')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Failed to fetch languages:', error);
    languages$.isLoading.set(false);
    return;
  }

  languages$.items.set(data || []);
  saveCachedLanguages(data || []);

  // Auto-select: find selected one, or first one, or null
  const selected = data?.find(l => l.is_selected);
  if (selected) {
    languages$.selectedCode.set(selected.language_code);
  } else if (data?.length > 0) {
    languages$.selectedCode.set(data[0].language_code);
    // Also update DB to mark first as selected
    await supabase
      .from('mellow_user_languages')
      .update({ is_selected: true })
      .eq('id', data[0].id);
  } else {
    languages$.selectedCode.set(null);
  }

  languages$.isLoading.set(false);
};

// Add a new language
export const addLanguage = async ({ languageCode, level }) => {
  const user = auth$.user.get();
  if (!user) throw new Error('Not authenticated');

  const items = languages$.items.get();
  const isFirst = items.length === 0;

  const { data, error } = await supabase
    .from('mellow_user_languages')
    .insert({
      user_id: user.id,
      language_code: languageCode,
      level: level,
      is_selected: isFirst, // Auto-select if first language
    })
    .select()
    .single();

  if (error) throw error;

  // Update local state and cache
  const allLanguages = [...items, data];
  languages$.items.set(allLanguages);
  saveCachedLanguages(allLanguages);

  if (isFirst) {
    languages$.selectedCode.set(languageCode);

    // Update user properties (Plan A - Essential User Context)
    updateUserProperties({
      current_language_code: languageCode,
      current_language_level: level,
    });
  }

  // Update total_languages user property (Plan A)
  updateUserProperties({
    total_languages: allLanguages.length,
  });

  // Update super properties (sent with every event)
  updateSuperProperties({
    learning_languages: allLanguages.map(l => l.language_code),
    primary_language: languageCode, // The one just added/selected
    language_count: allLanguages.length,
  });

  return data;
};

// Select a language
export const selectLanguage = async (languageCode) => {
  const user = auth$.user.get();
  if (!user) return;

  const items = languages$.items.get();

  // Update all to not selected, then set the chosen one
  await supabase
    .from('mellow_user_languages')
    .update({ is_selected: false })
    .eq('user_id', user.id);

  await supabase
    .from('mellow_user_languages')
    .update({ is_selected: true })
    .eq('user_id', user.id)
    .eq('language_code', languageCode);

  // Update local state
  languages$.selectedCode.set(languageCode);
  const updatedItems = items.map(l => ({ ...l, is_selected: l.language_code === languageCode }));
  languages$.items.set(updatedItems);

  // Update user properties (Plan A - Essential User Context)
  const selectedLang = updatedItems.find(l => l.language_code === languageCode);
  if (selectedLang) {
    updateUserProperties({
      current_language_code: languageCode,
      current_language_level: selectedLang.level,
    });
  }

  // Update super properties (primary language changes frequently)
  updateSuperProperties({
    primary_language: languageCode,
  });
};

// Update a language (change level)
export const updateLanguage = async ({ languageCode, level }) => {
  const user = auth$.user.get();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('mellow_user_languages')
    .update({ level })
    .eq('user_id', user.id)
    .eq('language_code', languageCode)
    .select()
    .single();

  if (error) throw error;

  // Update local state
  const items = languages$.items.get();
  languages$.items.set(
    items.map(l => l.language_code === languageCode ? { ...l, level } : l)
  );

  return data;
};

// Get languages array (helper)
export const getLanguagesArray = () => languages$.items.get() || [];

